import { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Div, Loader, Table, Text, useMediaQuery, useTheme } from "react-better-html";

import { type Workspace } from "../../types/workspace";
import { type ListRenderType } from "../../types/app";

import { useI18n } from "../../utils/i18n";

import WorkspaceCard from "./WorkspaceCard";

type WorkspacesListProps = {
   workspaces?: Workspace[];
   /** @default "grid" */
   renderType?: ListRenderType;
   flatList?: boolean;
   noLoading?: boolean;
   noEmptyState?: boolean;
};

function WorkspacesList({ workspaces, renderType = "grid", noLoading, noEmptyState }: WorkspacesListProps) {
   const theme = useTheme();
   const { t } = useI18n();
   const mediaQuery = useMediaQuery();
   const navigate = useNavigate();

   const onClickWorkspace = useCallback((workspace: Workspace, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event.metaKey || event.ctrlKey) window.open(`/workspaces/${workspace.id}`, "_blank");
      else navigate(`/workspaces/${workspace.id}`);
   }, []);
   const onClickTableRow = useCallback((item: Workspace) => {
      navigate(`/workspaces/${item.id}`);
   }, []);

   const filteredWorkspaces = useMemo(() => workspaces?.filter((workspace) => !workspace.archivedAt), [workspaces]);

   return filteredWorkspaces ? (
      filteredWorkspaces.length ? (
         renderType === "grid" ? (
            <Div.grid
               gridTemplateColumns={
                  mediaQuery.size600
                     ? "1fr 1fr"
                     : `repeat(auto-fill, minmax(${mediaQuery.size1000 ? 240 : 280}px, 1fr))`
               }
               gap={theme.styles.gap}
            >
               {filteredWorkspaces?.map((workspace) => (
                  <WorkspaceCard workspace={workspace} onClick={onClickWorkspace} key={workspace.id} />
               ))}
            </Div.grid>
         ) : (
            <Table
               columns={[
                  {
                     type: "element",
                     label: t((l) => l.common.workspace),
                     minWidth: 350,
                     render: (item) => (
                        <Div.row alignItems="center" gap={theme.styles.gap}>
                           <Div width={14} height={14} borderRadius={999} backgroundColor={item.color} />

                           <Text flex={1} fontWeight={700}>
                              {item.name}
                           </Text>
                        </Div.row>
                     ),
                  },
                  {
                     type: "text",
                     label: t((l) => l.common.items),
                     format: () => "0",
                  },
               ]}
               data={filteredWorkspaces}
               onClickRow={onClickTableRow}
            />
         )
      ) : !noEmptyState ? (
         <Text.unknown color={theme.colors.textSecondary + "a0"}>
            {t((l) => l.pages.workspaces.emptyStateMessage)}
         </Text.unknown>
      ) : undefined
   ) : !noLoading ? (
      <Loader.box />
   ) : undefined;
}

export default memo(WorkspacesList);
